import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { Loader2, Lock } from 'lucide-react';
import { useAuth } from '@/shared/contexts';
import {
  superAdminApi,
  type SuperAdminSupplierOrderSessionResponse
} from '@/pages/SuperAdmin/api/superAdminApi';
import { Alert } from '@/shared/components/ui/Alert';
import { Button } from '@/shared/components/ui/Button';
import { Input } from '@/shared/components/ui/Input';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle
} from '@/shared/components/ui/Dialog';

function extractApiError(err: unknown, fallback: string): string {
  const data = (err as { response?: { data?: unknown } })?.response?.data;
  const pick = (value: unknown): string | null => {
    if (value == null) return null;
    if (typeof value === 'string') return value;
    if (Array.isArray(value)) {
      return value.map(v => (typeof v === 'string' ? v : JSON.stringify(v))).join(', ');
    }
    if (typeof value === 'object' && value !== null && 'message' in value) {
      return pick((value as { message: unknown }).message);
    }
    return null;
  };
  return pick(data) || fallback;
}

export type OrderSessionPayload = SuperAdminSupplierOrderSessionResponse;

type Props = {
  open: boolean;
  supplierId: string;
  onClose: () => void;
  onSuccess: (session: OrderSessionPayload) => void;
};

export const OrderCreationCodeDialog: React.FC<Props> = ({
  open,
  supplierId,
  onClose,
  onSuccess
}) => {
  const { accessToken } = useAuth();
  const [code, setCode] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [submitting, setSubmitting] = useState(false);

  const handleClose = () => {
    if (submitting) return;
    setCode('');
    setError(null);
    onClose();
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!accessToken || !supplierId) return;
    const trimmed = code.trim();
    if (!trimmed) {
      setError('Enter the order creation code to continue');
      return;
    }

    setSubmitting(true);
    setError(null);
    try {
      const res = await superAdminApi.openSupplierOrderSession(
        accessToken,
        supplierId,
        trimmed
      );
      setCode('');
      onSuccess(res.data);
    } catch (err: unknown) {
      setError(extractApiError(err, 'Failed to verify order creation code'));
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={next => !next && handleClose()}>
      <DialogContent
        className="relative w-[min(100vw-2rem,28rem)] border-border-primary bg-background-card p-6"
        onClose={handleClose}
        onClick={e => e.stopPropagation()}
      >
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2 text-text-primary">
            <Lock className="h-4 w-4 text-[#006B82]" />
            Order creation code
          </DialogTitle>
          <DialogDescription className="text-text-tertiary">
            Enter your order creation code from Settings to place an order.
          </DialogDescription>
        </DialogHeader>

        <form className="space-y-4" onSubmit={e => void handleSubmit(e)}>
          <div>
            <label className="mb-1.5 block text-sm font-medium text-text-secondary">
              Code <span className="text-rose-500">*</span>
            </label>
            <Input
              type="password"
              autoComplete="off"
              autoFocus
              value={code}
              onChange={e => {
                setCode(e.target.value);
                setError(null);
              }}
              placeholder="Enter order creation code"
              className="bg-background-card"
              disabled={submitting}
            />
          </div>

          {error && <Alert variant="destructive">{error}</Alert>}

          <p className="text-sm text-text-tertiary">
            No code yet?{' '}
            <Link
              to="/super-admin/profile"
              className="font-medium text-[#006B82] hover:underline"
              onClick={handleClose}
            >
              Set it in Settings
            </Link>
          </p>

          <DialogFooter className="gap-2 sm:gap-2">
            <Button
              type="button"
              variant="outline"
              onClick={handleClose}
              disabled={submitting}
            >
              Cancel
            </Button>
            <Button
              type="submit"
              className="bg-[#006B82] hover:bg-[#005A6E]"
              disabled={submitting || !code.trim()}
            >
              {submitting && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
              {submitting ? 'Verifying…' : 'Continue'}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
};
